import api from './api';

/**
 * Admin API service for managing service pages and navigation
 * Provides methods for CRUD operations on service pages and navigation menus
 */
class AdminApiService {
  /**
   * Service Pages API methods
   */
  servicePages = {
    // Get all service pages with pagination and filters
    getAll: (params?: URLSearchParams) => {
      const queryString = params ? `?${params.toString()}` : '';
      return api.get(`/admin/service-pages/${queryString}`);
    },

    // Get single service page by ID
    getById: (id: number) => {
      return api.get(`/admin/service-pages?id=${id}`);
    },

    // Create new service page
    create: (data: any) => {
      return api.post('/admin/service-pages', data);
    },

    // Update existing service page
    update: (id: number, data: any) => {
      return api.put(`/admin/service-pages?id=${id}`, data);
    },

    // Delete service page
    delete: (id: number) => {
      return api.delete(`/admin/service-pages?id=${id}`);
    }
  };

  /**
   * Navigation API methods
   */
  navigation = {
    // Get all navigation menus with hierarchy
    getAll: (params?: URLSearchParams) => {
      const queryString = params ? `?${params.toString()}` : '';
      return api.get(`/admin/navigation${queryString}`);
    },

    // Get single navigation menu by ID
    getById: (id: number) => {
      return api.get(`/admin/navigation?id=${id}`);
    },

    // Create new navigation menu
    create: (data: any) => {
      return api.post('/admin/navigation', data);
    },

    // Update existing navigation menu
    update: (id: number, data: any) => {
      return api.put(`/admin/navigation?id=${id}`, data);
    },

    // Delete navigation menu
    delete: (id: number) => {
      return api.delete(`/admin/navigation?id=${id}`);
    },

    // Reorder navigation menu items
    reorder: (items: Array<{ id: number; display_order: number }>) => {
      return api.post('/admin/navigation?action=reorder', { items });
    }
  };

  /**
   * Section Management API methods
   */
  sections = {
    // Get all sections for a specific page
    getByPageId: (pageId: number) => {
      return api.get(`/admin/service-pages/sections?page_id=${pageId}`);
    },

    // Create new section
    create: (data: any) => {
      return api.post('/admin/service-pages/sections', data);
    },

    // Update existing section
    update: (id: number, data: any) => {
      return api.put(`/admin/service-pages/sections?id=${id}`, data);
    },

    // Delete section
    delete: (id: number) => {
      return api.delete(`/admin/service-pages/sections?id=${id}`);
    }
  };

  /**
   * Section Items Management API methods
   */
  sectionItems = {
    // Get all items for a specific section
    getBySectionId: (sectionId: number) => {
      return api.get(`/admin/service-pages/section-items?section_id=${sectionId}`);
    },

    // Create new section item
    create: (data: any) => {
      return api.post('/admin/service-pages/section-items', data);
    },

    // Update existing section item
    update: (id: number, data: any) => {
      return api.put(`/admin/service-pages/section-items?id=${id}`, data);
    },

    // Delete section item
    delete: (id: number) => {
      return api.delete(`/admin/service-pages/section-items?id=${id}`);
    },

    // Bulk update item order
    updateOrder: (items: Array<{ id: number; display_order: number }>) => {
      return api.post('/admin/service-pages/section-items?action=bulk-update-order', { items });
    }
  };

  /**
   * Countries API methods (for dropdowns and references)
   */
  countries = {
    // Get all countries
    getAll: () => {
      return api.get('/admin/countries');
    },

    // Get single country
    getById: (id: number) => {
      return api.get(`/admin/countries?id=${id}`);
    }
  };

  // Generic methods for backward compatibility
  get = (endpoint: string) => api.get(endpoint);
  post = (endpoint: string, data: any) => api.post(endpoint, data);
  put = (endpoint: string, data: any) => api.put(endpoint, data);
  delete = (endpoint: string) => api.delete(endpoint);
}

// Create and export singleton instance
const adminApi = new AdminApiService();
export { adminApi };
export default adminApi;